/**
 * VideoConfigTabContent — Config tab content for the Video Generator bottom panel.
 *
 * Contains: reference image picker + style slot (side by side), motion prompt
 * slot, control bar (config pill, retry selector, sparks count, generate
 * button), and the config flyout.
 *
 * The config flyout is rendered via FlyoutPortal to escape overflow-hidden clipping.
 */

import { memo, useState, useRef, useEffect, useCallback } from 'react'
import { VideoConfigFlyout, type VideoFrameRoleControl } from './VideoConfigFlyout'
import { StyleLibraryModal } from './StyleLibraryModal'
import { StyleSlot } from './StyleSlot'
import { ReferenceStyleRow } from './ReferenceStyleRow'
import { FlyoutPortal } from './FlyoutPortal'
import { useClickOutside } from './useClickOutside'
import { getVideoModelDisplayName } from './videoModelDisplayName'
import { getVideoResolutionDisplayName } from './videoResolutionDisplayName'
import { isProviderSelectedDuration } from './videoConstraints'
import { useVideoModels } from '../../../../hooks/useVideoModels'
import { videoSparksPerGeneration } from '../../../../generated/nodeDataTypes'
import { useManualGenerationCostEstimatesVisible } from '../../../../hooks/useGenerationCostEstimatesVisible'

/** Sparks shown on the regen pill = per-second rate × duration × retry count.
 * Backed by VIDEO_MODEL_USD_PER_SECOND (generated from google_pricing.json) so
 * the pill matches what _deduct_sparks will actually bill on submit.
 *
 * Resolve the duration the same way the backend does
 * (`_resolve_video_pricing`: `duration if duration > 0 else 5.0`) before
 * calling the mirror: `?? 5` upstream only catches null/undefined, not an
 * explicit 0 or NaN, so a 0 would otherwise yield a 0-sparks quote that
 * diverges from the 5s charge. The generated function stays generic (returns 0
 * for ≤0/unknown); the node-specific 5s default lives here at the call site. */
function getVideoSparks(
  model: string,
  resolution: string,
  durationSeconds: number,
  retryCount: number,
  referenceImageCount: number,
): number {
  const effDur = Number.isFinite(durationSeconds) && durationSeconds > 0 ? durationSeconds : 5
  return videoSparksPerGeneration(model, resolution, effDur, referenceImageCount) * Math.max(1, retryCount)
}

export interface VideoConfigTabContentProps {
  nodeId?: string
  model: string
  currentStyleId?: string
  styleConnected?: boolean
  onStyleChange?: (styleId: string) => void
  onModelChange: (model: string) => void
  referenceImagePicker?: React.ReactNode
  referenceImageCount?: number
  motionPromptContent?: React.ReactNode
  duration: number
  resolution: string
  aspectRatio: string
  providerGenerateAudio: boolean
  onDurationChange: (duration: number) => void
  onResolutionChange: (resolution: string) => void
  onAspectRatioChange: (aspectRatio: string) => void
  onProviderGenerateAudioChange: (providerGenerateAudio: boolean) => void
  allowBgm: boolean
  onAllowBgmChange: (allowBgm: boolean) => void
  isRunning: boolean
  onGenerate: (retryCount: number) => void
  onCancel?: () => void
  isCancelling?: boolean
  activeTab: string
  /**
   * When false, the Generate button is disabled (e.g. because the structured
   * motion prompt has overlapping video segments). Defaults to true so
   * existing consumers (ShortVideoNode) are unaffected.
   */
  canGenerate?: boolean
  /** Tooltip shown on the disabled Generate button. */
  cannotGenerateReason?: string
  /** User-facing warning when the saved model is no longer selectable. */
  modelUnavailableReason?: string
  /** Declared approximation of the current mode on this model (already localized). */
  modeNotice?: string
  /** Raw saved model ID to show in the warning. */
  unavailableModelId?: string
  /** Passive note on how the connected images are used, e.g. first + last frame. */
  referenceNotice?: string
  /** Opening / Closing frame row in the settings flyout (RFC-1668); omitted hides it. */
  frameRole?: VideoFrameRoleControl
  /**
   * Wraps the prompt area (References | Style row + motion prompt) before it
   * renders. The video node uses it to move that area into its expanded
   * popup and leave a placeholder here. Defaults to rendering it in place.
   */
  renderPromptArea?: (area: React.ReactNode) => React.ReactNode
}

export const VideoConfigTabContent = memo(function VideoConfigTabContent({
  nodeId,
  model,
  currentStyleId,
  styleConnected = false,
  onStyleChange,
  onModelChange,
  referenceImagePicker,
  referenceImageCount = 0,
  motionPromptContent,
  duration,
  resolution,
  aspectRatio,
  providerGenerateAudio,
  onDurationChange,
  onResolutionChange,
  onAspectRatioChange,
  onProviderGenerateAudioChange,
  allowBgm,
  onAllowBgmChange,
  isRunning,
  onGenerate,
  onCancel,
  isCancelling,
  activeTab,
  canGenerate = true,
  cannotGenerateReason,
  modelUnavailableReason,
  modeNotice,
  unavailableModelId,
  referenceNotice,
  frameRole,
  renderPromptArea,
}: VideoConfigTabContentProps) {
  const [isStyleModalOpen, setIsStyleModalOpen] = useState(false)
  const [retryCount, setRetryCount] = useState(1)
  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false)
  const [isRetryPopoverOpen, setIsRetryPopoverOpen] = useState(false)

  const pillRef = useRef<HTMLButtonElement>(null)
  const retryPillRef = useRef<HTMLButtonElement>(null)
  const retryPopoverRef = useRef<HTMLDivElement>(null)

  const closeFlyout = useCallback(() => setIsFlyoutOpen(false), [])
  const closeRetryPopover = useCallback(() => setIsRetryPopoverOpen(false), [])

  // Resolve BYOM model display name from the byom array (single source of truth).
  const { byom: byomModels } = useVideoModels()
  const modelDisplayName = getVideoModelDisplayName(model, byomModels)

  // Computed values
  const isModelUnavailable = Boolean(modelUnavailableReason)
  const effectiveCanGenerate = canGenerate !== false && !isModelUnavailable
  const effectiveCannotGenerateReason = modelUnavailableReason ?? cannotGenerateReason
  const sparksCount = getVideoSparks(
    model,
    resolution,
    duration,
    retryCount,
    referenceImageCount,
  )
  const showManualEstimate = useManualGenerationCostEstimatesVisible()
  const modelAudioLabel = providerGenerateAudio ? 'Model audio' : 'No model audio'
  const bgmLabel = allowBgm ? 'BGM' : 'No BGM'
  const durationLabel = isProviderSelectedDuration(model) ? 'Auto duration' : `${duration}s`
  const configPillLabel = isModelUnavailable
    ? 'Model unavailable · Select model'
    : `${modelDisplayName} · ${aspectRatio} · ${getVideoResolutionDisplayName(model, resolution)} · ${durationLabel} · ${modelAudioLabel} · ${bgmLabel}`

  // Close flyout when switching away from config tab
  useEffect(() => {
    if (activeTab !== 'config') {
      setIsFlyoutOpen(false)
    }
  }, [activeTab])

  // Click-outside for retry popover
  useClickOutside([retryPopoverRef, retryPillRef], closeRetryPopover, isRetryPopoverOpen)

  // Generate handler
  const handleGenerate = useCallback(() => {
    onGenerate(retryCount)
  }, [onGenerate, retryCount])

  // References | Style row + motion prompt. `renderPromptArea` lets the
  // caller move this whole area elsewhere (the video node's expanded popup).
  const promptArea = (
    <>
      {/* References | Style, split by a divider (see ReferenceStyleRow). */}
      <ReferenceStyleRow
        className="shrink-0"
        references={referenceImagePicker}
        style={
          onStyleChange && (
            <StyleSlot
              currentStyleId={currentStyleId}
              onOpen={() => setIsStyleModalOpen(true)}
              connected={styleConnected}
            />
          )
        }
      />
      {motionPromptContent}
    </>
  )

  return (
    <div className="flex flex-col h-full nowheel min-h-0">
      {/* Config content — flex column that fills the panel height. References
          stay fixed at the top; the motion-prompt slot (motionPromptContent's
          own flex-1 root) grows to fill, so its prompt box reaches the control
          bar with no void below — matching ImageGenerator's filling prompt. */}
      <div className="flex-1 min-h-0 p-3 flex flex-col gap-3">
        {renderPromptArea ? renderPromptArea(promptArea) : promptArea}
      </div>

      {referenceNotice && !isModelUnavailable && (
        <div
          className="mx-3 mb-1 rounded-lg border border-border-subtle bg-surface-2 px-2 py-1 text-xs text-text-muted"
          data-testid="video-reference-notice"
          role="note"
        >
          {referenceNotice}
        </div>
      )}

      {isModelUnavailable && (
        <div
          id="video-model-unavailable-alert"
          className="mx-3 mb-1 rounded-lg border border-warning/30 bg-warning/10 px-2 py-1 text-xs font-medium text-warning"
          data-testid="video-model-unavailable-alert"
          role="alert"
          aria-live="polite"
        >
          <span>{modelUnavailableReason}</span>
          {unavailableModelId && (
            <span className="ml-1 break-all font-mono text-xxs text-warning/90" data-testid="video-unavailable-model-id">
              {unavailableModelId}
            </span>
          )}
        </div>
      )}

      {modeNotice && !isModelUnavailable && (
        <div
          className="mx-3 mb-1 rounded-lg border border-border-subtle bg-surface-2 px-2 py-1 text-xs text-text-muted"
          data-testid="video-mode-notice"
          role="note"
        >
          {modeNotice}
        </div>
      )}

      {/* Control Bar -- fixed at bottom */}
      <div className="flex items-center gap-2 px-3 py-1 border-t border-border-subtle" data-testid="video-control-bar">
        {/* Config Pill */}
        <button
          ref={pillRef}
          onClick={() => setIsFlyoutOpen(!isFlyoutOpen)}
          className={`min-w-0 truncate px-2.5 py-0.5 text-xs font-medium rounded-full border transition-colors whitespace-nowrap ${
            isModelUnavailable
              ? 'bg-warning/10 text-warning border-warning/40 hover:bg-warning/15 hover:border-warning/60'
              : 'bg-surface-2 text-text-muted hover:text-foreground border-border-subtle hover:border-primary/40'
          }`}
          data-testid="video-config-pill"
          data-model-validity={isModelUnavailable ? 'invalid' : 'valid'}
          aria-invalid={isModelUnavailable ? 'true' : undefined}
          aria-describedby={isModelUnavailable ? 'video-model-unavailable-alert' : undefined}
          title={isModelUnavailable ? modelUnavailableReason : configPillLabel}
        >
          {configPillLabel}
        </button>

        {/* Config Flyout — portal-based */}
        <FlyoutPortal
          triggerRef={pillRef}
          isOpen={isFlyoutOpen}
          onClose={closeFlyout}
          minWidth={0}
          className="w-video-config-flyout max-w-video-config-flyout-viewport"
        >
          <VideoConfigFlyout
            nodeId={nodeId}
            referenceImageCount={referenceImageCount}
            model={model}
            onModelChange={onModelChange}
            aspectRatio={aspectRatio}
            onAspectRatioChange={onAspectRatioChange}
            resolution={resolution}
            onResolutionChange={onResolutionChange}
            duration={duration}
            onDurationChange={onDurationChange}
            providerGenerateAudio={providerGenerateAudio}
            onProviderGenerateAudioChange={onProviderGenerateAudioChange}
            allowBgm={allowBgm}
            onAllowBgmChange={onAllowBgmChange}
            modelUnavailableReason={modelUnavailableReason}
            unavailableModelId={unavailableModelId}
            frameRole={frameRole}
          />
        </FlyoutPortal>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Retry Count Pill + Popover */}
        <div className="relative shrink-0" data-testid="video-retry-selector">
          <button
            ref={retryPillRef}
            onClick={() => setIsRetryPopoverOpen(!isRetryPopoverOpen)}
            className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-surface-2 text-text-muted hover:text-foreground border border-border-subtle hover:border-primary/40 transition-colors whitespace-nowrap"
            data-testid="video-retry-pill"
          >
            {retryCount}x
          </button>

          {isRetryPopoverOpen && (
            <div
              ref={retryPopoverRef}
              className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-surface-1 border border-border rounded-lg shadow-2xl overflow-hidden z-50 min-w-[48px]"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              data-testid="video-retry-popover"
            >
              {([4, 3, 2, 1]).map((n) => (
                <button
                  key={n}
                  onClick={() => {
                    setRetryCount(n)
                    setIsRetryPopoverOpen(false)
                  }}
                  className={`w-full px-3 py-1.5 text-xs font-medium text-center transition-colors ${
                    retryCount === n
                      ? 'text-primary-foreground'
                      : 'text-text-muted hover:text-foreground hover:bg-surface-2'
                  }`}
                  style={
                    retryCount === n
                      ? { backgroundColor: 'var(--primary)' }
                      : undefined
                  }
                  data-testid={`video-retry-${n}x`}
                >
                  {n}x
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Sparks Count */}
        {showManualEstimate && (
          <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-text-muted tabular-nums" data-testid="video-sparks-count">
            <svg className="w-3.5 h-3.5 text-yellow-400 shrink-0" viewBox="0 0 24 24" fill="currentColor">
              <path d="M13 3L4 14h7l-2 7 9-11h-7l2-7z" />
            </svg>
            {sparksCount}
          </span>
        )}

        {/* Generate / Stop Button */}
        {isRunning && onCancel ? (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onCancel()
            }}
            disabled={isCancelling}
            className="w-6 h-6 shrink-0 flex items-center justify-center rounded-full border border-destructive/50 bg-destructive/15 text-destructive hover:bg-destructive/25 hover:border-destructive transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            title={isCancelling ? 'Stopping...' : 'Stop'}
            data-testid="cancel-node-button"
          >
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="6" width="12" height="12" rx="1" />
            </svg>
          </button>
        ) : (
          <button
            onClick={handleGenerate}
            disabled={isRunning || !effectiveCanGenerate}
            title={!effectiveCanGenerate ? effectiveCannotGenerateReason : 'Generate'}
            className="w-6 h-6 shrink-0 flex items-center justify-center rounded-full bg-primary hover:bg-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="video-generate-button"
            data-model-validity={isModelUnavailable ? 'invalid' : 'valid'}
          >
            <svg className="w-3.5 h-3.5 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
          </button>
        )}
      </div>
      {onStyleChange && (
        <StyleLibraryModal
          isOpen={isStyleModalOpen}
          onClose={() => setIsStyleModalOpen(false)}
          onApply={(styleId) => {
            onStyleChange(styleId)
            setIsStyleModalOpen(false)
          }}
          currentStyleId={currentStyleId ?? ''}
        />
      )}
    </div>
  )
})

VideoConfigTabContent.displayName = 'VideoConfigTabContent'
